import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { GoogleMap, useLoadScript, Circle, OverlayView, InfoWindow } from '@react-google-maps/api';
import {
  Alert,
  Box,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import {
  attachLabelPositions,
  filterOverlappingMarkers,
  getChannelColor,
} from '../../utils/channelMapUtils';

const GOOGLE_MAPS_API_KEY = 'AIzaSyDBzNIMvmobitJ-vi8Z-kzVfK99qSb7jRw';
const GOOGLE_MAPS_LIBRARIES = ['geometry', 'marker'];

const defaultCenter = { lat: 32.0853, lng: 34.7818 };

const CircleLabel = ({ marker, strokeColor }) => (
  <Box
    sx={{
      display: 'inline-flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 46,
      px: 0.6,
      py: 0.35,
      borderRadius: 1,
      bgcolor: 'rgba(255, 255, 255, 0.94)',
      border: '1.5px solid',
      borderColor: strokeColor,
      boxShadow: '0 1px 4px rgba(0,0,0,0.22)',
      pointerEvents: 'none',
      userSelect: 'none',
      lineHeight: 1.1,
      textAlign: 'center',
    }}
  >
    <Typography component="span" sx={{ fontSize: '0.82rem', fontWeight: 800, color: '#111' }}>
      ch {marker.channel}
    </Typography>
    <Typography component="span" sx={{ fontSize: '0.62rem', fontWeight: 600, color: '#333' }}>
      {marker.frequencyMHz} MHz
    </Typography>
    <Typography component="span" sx={{ fontSize: '0.58rem', fontWeight: 700, color: '#555', fontFamily: 'monospace' }}>
      {marker.serialSuffix}
    </Typography>
  </Box>
);

const ChannelMapView = ({
  markers,
  conflictIds,
  markerRadiusM,
  height = 520,
  selectedMarkerId,
  onSelectMarker,
  emptyMessage,
  hasAnalyzedApRadios,
  showOverlappingOnly = true,
  onShowOverlappingOnlyChange,
}) => {
  const theme = useTheme();
  const mapRef = useRef(null);
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const displayedMarkers = useMemo(() => {
    const base = showOverlappingOnly
      ? filterOverlappingMarkers(markers, conflictIds)
      : markers;
    return attachLabelPositions(base);
  }, [markers, conflictIds, showOverlappingOnly]);

  const mapColorScheme = useMemo(() => {
    if (!isLoaded || !window.google?.maps?.ColorScheme) return undefined;
    return theme.palette.mode === 'dark'
      ? window.google.maps.ColorScheme.DARK
      : window.google.maps.ColorScheme.LIGHT;
  }, [isLoaded, theme.palette.mode]);

  const mapCenter = useMemo(() => {
    if (!displayedMarkers.length) return defaultCenter;
    const lat = displayedMarkers.reduce((sum, m) => sum + m.lat, 0) / displayedMarkers.length;
    const lng = displayedMarkers.reduce((sum, m) => sum + m.lng, 0) / displayedMarkers.length;
    return { lat, lng };
  }, [displayedMarkers]);

  const fitToMarkers = useCallback(() => {
    const map = mapRef.current;
    if (!map || !displayedMarkers.length || !window.google?.maps) return;
    const bounds = new window.google.maps.LatLngBounds();
    displayedMarkers.forEach((m) => {
      bounds.extend({ lat: m.lat, lng: m.lng });
      bounds.extend({ lat: m.labelLat, lng: m.labelLng });
    });
    map.fitBounds(bounds, 48);
  }, [displayedMarkers]);

  const handleMapLoad = useCallback(
    (map) => {
      mapRef.current = map;
      fitToMarkers();
    },
    [fitToMarkers],
  );

  useEffect(() => {
    fitToMarkers();
  }, [fitToMarkers]);

  const handleViewChange = (_event, value) => {
    if (value) onShowOverlappingOnlyChange?.(value === 'overlapping');
  };

  if (loadError) {
    return <Alert severity="error">Failed to load Google Maps.</Alert>;
  }

  if (!isLoaded) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', height, borderRadius: 1, overflow: 'hidden', border: 1, borderColor: 'divider' }}>
      <Box
        sx={{
          position: 'absolute',
          top: 10,
          right: 10,
          zIndex: 2,
          bgcolor: 'background.paper',
          borderRadius: 1,
          boxShadow: 2,
        }}
      >
        <ToggleButtonGroup
          size="small"
          exclusive
          value={showOverlappingOnly ? 'overlapping' : 'all'}
          onChange={handleViewChange}
          aria-label="Map AP filter"
        >
          <ToggleButton value="overlapping" sx={{ px: 1.25, py: 0.5, fontSize: '0.75rem' }}>
            Conflicts only
          </ToggleButton>
          <ToggleButton value="all" sx={{ px: 1.25, py: 0.5, fontSize: '0.75rem' }}>
            All APs
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {!displayedMarkers.length ? (
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'action.hover',
          }}
        >
          <Typography color="text.secondary" sx={{ px: 2, textAlign: 'center' }}>
            {emptyMessage ||
              (hasAnalyzedApRadios
                ? showOverlappingOnly
                  ? 'No heard-pair spectrum conflicts at the current settings. Switch to “All APs” to see every jnet2 backhaul radio.'
                  : 'No jnet2 AP radios with GPS to plot.'
                : 'No jnet2 AP radios with GPS to plot on the map.')}
          </Typography>
        </Box>
      ) : (
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          center={mapCenter}
          zoom={12}
          onLoad={handleMapLoad}
          options={{
            streetViewControl: false,
            mapTypeControl: true,
            fullscreenControl: true,
            mapId: 'RRM_CHANNEL_MAP',
            colorScheme: mapColorScheme,
          }}
        >
          {displayedMarkers.map((marker) => {
            const isConflict = conflictIds.has(marker.id);
            const color = getChannelColor(marker.channel, marker.band, isConflict);
            const circleCenter = { lat: marker.lat, lng: marker.lng };
            const labelPosition = { lat: marker.labelLat, lng: marker.labelLng };
            return (
              <React.Fragment key={marker.id}>
                <Circle
                  center={circleCenter}
                  radius={markerRadiusM}
                  options={{
                    fillColor: color,
                    fillOpacity: isConflict ? 0.22 : 0.14,
                    strokeColor: color,
                    strokeOpacity: isConflict ? 0.9 : 0.6,
                    strokeWeight: isConflict ? 2.5 : 1.5,
                    clickable: true,
                  }}
                  onClick={() => onSelectMarker?.(marker.id)}
                />
                <OverlayView
                  position={labelPosition}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                  getPixelPositionOffset={(width, height) => ({
                    x: -(width / 2),
                    y: -(height / 2),
                  })}
                >
                  <CircleLabel marker={marker} strokeColor={color} />
                </OverlayView>
              </React.Fragment>
            );
          })}

          {selectedMarkerId && (() => {
            const selectedMarker = displayedMarkers.find((m) => m.id === selectedMarkerId);
            if (!selectedMarker) return null;
            return (
              <InfoWindow
                position={{ lat: selectedMarker.labelLat, lng: selectedMarker.labelLng }}
                onCloseClick={() => onSelectMarker?.(null)}
              >
                <Box sx={{ minWidth: 180, color: '#000' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {selectedMarker.serial}
                    {selectedMarker.isCap ? ' (CAP)' : ''}
                  </Typography>
                  <Typography variant="body2">
                    {selectedMarker.band} · ch {selectedMarker.channel} ({selectedMarker.frequencyMHz} MHz)
                  </Typography>
                  <Typography variant="caption" display="block">
                    Radio: {selectedMarker.radioName}
                  </Typography>
                  {selectedMarker.channelWidth ? (
                    <Typography variant="caption" display="block">
                      Width: {selectedMarker.channelWidth} MHz
                    </Typography>
                  ) : null}
                  {conflictIds.has(selectedMarker.id) ? (
                    <Typography variant="caption" display="block" color="error.main">
                      In a heard-pair spectrum conflict at current settings
                    </Typography>
                  ) : null}
                </Box>
              </InfoWindow>
            );
          })()}
        </GoogleMap>
      )}
    </Box>
  );
};

export default ChannelMapView;
